import { N900 } from '@atlaskit/theme/colors';
import {
  FormattedBreakdownPriceTier,
  FormattedPriceCalculationResponse
} from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import PricingExampleIcon from '../../../icons/pricing-example-icon';
import { deviceMediaQueries } from '../../../utils';
import { PricingExampleHeader } from './pricing-example-header';

export type PricingExampleMonthlyProps = {
  unitCounts: number[];
  priceCalc: FormattedPriceCalculationResponse;
  showFirstFlatRateTier: boolean;
};

export const PricingExampleMonthly = ({
  unitCounts,
  priceCalc,
  showFirstFlatRateTier
}: PricingExampleMonthlyProps) => {
  const {
    hasFlatRateFirstTier,
    formattedTotalPrice,
    formattedAvgPerUnitPrice,
    formattedBreakdown
  } = priceCalc;

  return (
    <>
      <PricingExampleHeader unitCount={unitCounts[0] || 0} />
      <DetailContainer>
        {showFirstFlatRateTier && (
          <FlatRateIconContainer>
            <PricingExampleIcon />
          </FlatRateIconContainer>
        )}
        <PriceBreakDownContainer>
          <UserCount>
            <BoldText>
              <FormattedMessage
                id="userCountLabel"
                defaultMessage="User count"
                description="Label for user count"
              />
            </BoldText>
          </UserCount>

          <PriceBreakDownListingContainer>
            {formattedBreakdown.map(
              (fmtBreakdownTier: FormattedBreakdownPriceTier, idx: number) => {
                const isFlatRateTier =
                  showFirstFlatRateTier && hasFlatRateFirstTier && idx == 0;

                return (
                  <React.Fragment key={idx}>
                    <NumUnits>{fmtBreakdownTier.formattedUnitsInTier}</NumUnits>
                    <UsersLabel>
                      <FormattedMessage
                        id="userLabel"
                        defaultMessage="users @"
                        description="Label for users"
                      />
                    </UsersLabel>
                    <TierPrice>
                      {isFlatRateTier
                        ? fmtBreakdownTier.formattedTotalPriceForTier
                        : fmtBreakdownTier.formattedAvgPerUnitPriceForTier}
                    </TierPrice>
                    {isFlatRateTier && (
                      <FlatRateMessageContainer>
                        <FormattedMessage
                          id="flatRateFirstTierLabel"
                          defaultMessage="flat rate"
                          description="Label for flat rate first tier"
                        />
                      </FlatRateMessageContainer>
                    )}
                  </React.Fragment>
                );
              }
            )}
          </PriceBreakDownListingContainer>

          <Border />
          <div className="users">
            <PriceBreakDownListingContainer>
              <TotalUnits>{unitCounts[0]}</TotalUnits>
              <TotalUsersLabel>
                <FormattedMessage
                  id="pricingUserMessage"
                  defaultMessage="users"
                  description="pricing for number of users"
                />
              </TotalUsersLabel>
            </PriceBreakDownListingContainer>
          </div>
          <div className="monthly-cost">
            <RegularText>
              <FormattedMessage
                id="monthlyCostLabel"
                defaultMessage="Monthly cost: "
                description="Monthly cost for users"
              />
            </RegularText>{' '}
            <BoldText>{formattedTotalPrice}</BoldText>
          </div>
          <div className="average-cost">
            <RegularText>
              <FormattedMessage
                id="annualCostPerUserMessage"
                defaultMessage="Estimated price per user: "
                description="Annual cost per user"
              />
            </RegularText>{' '}
            <BoldText>{formattedAvgPerUnitPrice}</BoldText>
          </div>
        </PriceBreakDownContainer>
        {!showFirstFlatRateTier && (
          <IconContainer>
            <PricingExampleIcon />
          </IconContainer>
        )}
      </DetailContainer>
    </>
  );
};

const DetailContainer = styled.div`
  position: relative;
`;

const UserCount = styled.div`
  margin-bottom: 4px;
  margin-top: 16px;

  ${deviceMediaQueries.desktop} {
    margin-bottom: 8px;
  }
`;

const IconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 15%;

  svg {
    height: 68px;
    width: 68px;
  }

  ${deviceMediaQueries.desktop} {
    right: 0;
    top: 0;

    svg {
      height: 160px;
      width: 160px;
    }
  }
`;

const FlatRateIconContainer = styled(IconContainer)`
  ${deviceMediaQueries.extraSmall} {
    display: block;
    margin-left: auto;
    margin-right: auto;
    padding-top: 16px;
    ${deviceMediaQueries.desktop} {
      position: relative;
    }
    width: 20%;

    svg {
      height: 75px;
      width: 75px;
    }
  }
`;

const PriceBreakDownContainer = styled.div`
  font-family: 'Charlie Text';
  font-size: 14px;
  line-height: 18px;

  .users {
    padding-top: 8px;
  }

  .monthly-cost {
    padding-top: 12px;
  }

  .average-cost {
    padding-bottom: 24px;
    padding-top: 4px;
  }

  ${deviceMediaQueries.desktop} {
    font-size: 16px;
    line-height: 24px;
  }
`;

const PriceBreakDownListingContainer = styled.span`
  display: inline-grid;
  grid-template-columns: repeat(4, auto);
`;

const RegularText = styled.span`
  font-weight: 400;
`;

const NumUnits = styled.span`
  grid-column: 1;
  text-align: right;
`;

const TotalUnits = styled.span`
  font-weight: 700;
  text-align: right;
`;

const UsersLabel = styled.span`
  padding-left: 8px;
`;

const TotalUsersLabel = styled.span`
  font-weight: 700;
  grid-column: 2;
  padding-left: 7px;
`;

const TierPrice = styled.span`
  font-weight: 700;
  grid-column: 3;
  padding-left: 10px;
  text-align: right;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

const Border = styled.div`
  border-top: 1px solid ${N900};
  margin-top: 8px;
  width: 140px;

  ${deviceMediaQueries.desktop} {
    width: 180px;
  }
`;

const FlatRateMessageContainer = styled.span`
  font-weight: 700;
  grid-column: 4;
  padding-left: 10px;
`;
